/* ==========================================================================
   Cart drawer controller (new design)
   - Vanilla JS, self-contained (no theme cart code).
   - PubSub for cart state changes.
   - Cart mutations via Shopify Cart AJAX API (fetch).
   - Live re-render via Section Rendering API.
   - Subscription (Appstle / native selling plans) upgrade.
   - Addon products carousel with synced snap-scroll (arrows + dots).
   - Intercepts external add-to-cart (theme jQuery + fetch) to refresh + open.
   ========================================================================== */
(function () {
  'use strict';

  var SECTION_ID = 'pmt-pvt-cart-drawer';
  var CART_MUTATION_RE = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/i;
  var ADD_RE = /\/cart\/add(\.js)?(\?|$)/i;

  /* ---- PubSub ------------------------------------------------------------ */
  // Shared bus so product / drawer / other modules can talk without tight coupling.
  var PubSub = window.PmtPubSub || (function () {
    var subscribers = {};
    return {
      subscribe: function (event, cb) {
        (subscribers[event] = subscribers[event] || []).push(cb);
        return function () {
          subscribers[event] = (subscribers[event] || []).filter(function (fn) {
            return fn !== cb;
          });
        };
      },
      publish: function (event, data) {
        (subscribers[event] || []).forEach(function (cb) {
          try { cb(data); } catch (e) { /* no-op */ }
        });
      }
    };
  })();
  window.PmtPubSub = PubSub;

  var EVENTS = {
    added: 'cart:added',
    updated: 'cart:updated',
    opened: 'cart:opened',
    closed: 'cart:closed',
    error: 'cart:error'
  };

  /* ---- Helpers ----------------------------------------------------------- */
  function root() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  function getDrawer() {
    return document.getElementById('desktop_cart');
  }

  function getOverlay() {
    return document.querySelector('.sidebar-overlay');
  }

  var isMobile = function () {
    return window.matchMedia('(max-width: 767px)').matches;
  };

  // Guards double-refresh: our own fetch calls are flagged so the global
  // interceptor does not react to them (we refresh explicitly instead).
  var internalCall = 0;

  function fetchJSON(url, options) {
    return fetch(url, options).then(function (res) {
      return res.json().catch(function () { return {}; }).then(function (body) {
        if (!res.ok) {
          var err = new Error((body && body.description) || 'Cart request failed');
          err.data = body;
          throw err;
        }
        return body;
      });
    });
  }

  /* ---- Open / close ------------------------------------------------------ */
  function openDrawer(shouldRefresh) {
    var drawer = getDrawer();
    if (!drawer) return;
    var overlay = getOverlay();
    if (overlay) overlay.classList.add('act');
    drawer.classList.add('active');
    drawer.setAttribute('aria-hidden', 'false');
    if (isMobile()) document.body.classList.add('open-canvans-cart');
    PubSub.publish(EVENTS.opened);
    if (shouldRefresh !== false) refresh();
  }

  function closeDrawer() {
    var drawer = getDrawer();
    if (!drawer) return;
    // Clear every backdrop the theme may have toggled open (the legacy theme
    // also sets a `data-close` flag on the overlay), so nothing lingers.
    var overlays = document.querySelectorAll('.sidebar-overlay, .canvas-overlay');
    for (var i = 0; i < overlays.length; i++) {
      overlays[i].classList.remove('act');
      overlays[i].removeAttribute('data-close');
    }
    drawer.classList.remove('active');
    drawer.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('open-canvans-cart');
    PubSub.publish(EVENTS.closed);
  }

  /* ---- Re-render (Section Rendering API) --------------------------------- */
  var refreshPromise = null;

  function refresh() {
    var url = root() + '?sections=' + SECTION_ID;
    refreshPromise = fetch(url)
      .then(function (res) { return res.json(); })
      .then(function (data) {
        var html = data && data[SECTION_ID];
        if (!html) return;
        var parser = document.createElement('div');
        parser.innerHTML = html;
        var fresh = parser.querySelector('[data-cart-drawer-content]');
        var live = document.querySelector('#desktop_cart [data-cart-drawer-content]');
        if (fresh && live) {
          live.innerHTML = fresh.innerHTML;
          syncGlobalCount(live);
          localizeShippingBar();
          applyCurrency();
          initSliders();
          PubSub.publish(EVENTS.updated);
        }
      })
      .catch(function () { /* keep current DOM on failure */ });
    return refreshPromise;
  }

  // Localize the free-shipping threshold + progress into the shopper's
  // presentment currency using Shopify Markets' conversion rate.
  // settings.free_shipping is a plain number in the shop's BASE currency, while
  // cart.total_price is already rendered in the PRESENTMENT currency under
  // Markets, so the two must be reconciled here (Liquid has no access to the
  // live rate). On a non-Markets store the rate is 1 and this is a no-op.
  function formatMoney(cents, currencyCode) {
    var amount = cents / 100;
    if (currencyCode && window.Intl && Intl.NumberFormat) {
      try {
        return new Intl.NumberFormat(undefined, {
          style: 'currency',
          currency: currencyCode
        }).format(amount);
      } catch (e) { /* fall through to a plain number */ }
    }
    return amount.toFixed(2);
  }

  function localizeShippingBar() {
    try {
      var bar = document.querySelector('#desktop_cart [data-shipping-bar]');
      if (!bar) return;

      var thresholdBase = parseInt(bar.getAttribute('data-threshold-base-cents'), 10) || 0;
      var totalCents = parseInt(bar.getAttribute('data-total-cents'), 10) || 0;
      if (thresholdBase <= 0) return;

      var cur = window.Shopify && window.Shopify.currency;
      var rate = cur ? parseFloat(cur.rate) : NaN;
      var active = cur && cur.active;
      var base = window.shopCurrency;
      // No usable rate → keep the server-rendered base-currency values as-is.
      if (!isFinite(rate) || rate <= 0) return;
      // Shop currency == presentment currency and no scaling → nothing to do.
      if (rate === 1 && (!active || !base || active === base)) return;

      var thresholdCents = Math.round(thresholdBase * rate);
      var remainingCents = Math.max(0, thresholdCents - totalCents);
      var pct = Math.max(0, Math.min(100, Math.round((totalCents / thresholdCents) * 100)));
      var unlocked = remainingCents <= 0;

      var remainingEl = bar.querySelector('[data-shipping-remaining]');
      var unlockedEl = bar.querySelector('[data-shipping-unlocked]');
      if (remainingEl) remainingEl.hidden = unlocked;
      if (unlockedEl) unlockedEl.hidden = !unlocked;

      var amountEl = bar.querySelector('[data-shipping-amount]');
      if (amountEl && !unlocked) amountEl.textContent = formatMoney(remainingCents, active);

      var progress = bar.querySelector('[data-shipping-progress]');
      var knob = bar.querySelector('[data-shipping-knob]');
      if (progress) progress.style.width = pct + '%';
      if (knob) knob.style.left = pct + '%';
    } catch (e) { /* leave server-rendered values on failure */ }
  }

  // Localize freshly rendered money into the shopper's selected currency using
  // the theme's JS currency converter. Scoped to the drawer so the rest of the
  // page's already-converted prices are not double-converted.
  function applyCurrency() {
    try {
      var $ = window.jQuery;
      var Cur = window.Currency;
      if (!$ || !Cur || typeof Cur.convertAll !== 'function') return;
      var base = window.shopCurrency || Cur.currentCurrency;
      var selected = $('#currencies span.selected').attr('data-currency') || Cur.currentCurrency || base;
      if (!base || !selected || selected === base) return;
      var $spans = $('#desktop_cart span.money');
      // Store the base-currency value so the converter reads the correct source.
      $spans.each(function () {
        if (!this.getAttribute('data-currency-' + base)) {
          this.setAttribute('data-currency-' + base, this.innerHTML);
        }
      });
      Cur.convertAll(base, selected, $spans);
    } catch (e) { /* leave base currency on failure */ }
  }

  function syncGlobalCount(scope) {
    var badge = (scope || document).querySelector('[data-cart-count]');
    if (!badge) return;
    var count = badge.textContent.trim();
    ['CartCount', 'CartCountCavas'].forEach(function (id) {
      document.querySelectorAll('[id="' + id + '"]').forEach(function (el) {
        el.textContent = count;
      });
    });
  }

  /* ---- Cart mutations ---------------------------------------------------- */
  // Increments the guard BEFORE the fetch is initiated so the global
  // interceptor ignores our own request, then releases it afterwards.
  function withInternal(fn) {
    internalCall++;
    var promise;
    try {
      promise = fn();
    } catch (e) {
      internalCall = Math.max(0, internalCall - 1);
      throw e;
    }
    return promise.finally(function () {
      internalCall = Math.max(0, internalCall - 1);
    });
  }

  function changeLine(key, quantity, extra) {
    var body = Object.assign({ id: key, quantity: quantity }, extra || {});
    return withInternal(function () {
      return fetchJSON(root() + 'cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body)
      });
    });
  }

  function addLine(variantId, quantity, extra) {
    var item = Object.assign({
      id: Number(variantId),
      quantity: quantity || 1
    }, extra || {});
    return withInternal(function () {
      return fetchJSON(root() + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [item] })
      });
    });
  }

  function setBusy(el, busy) {
    if (!el) return;
    el.classList.toggle('is-loading', !!busy);
  }

  /* ---- Line item interactions (event delegation) ------------------------ */
  function currentQty(itemEl) {
    var v = itemEl && itemEl.querySelector('[data-qty-value]');
    return v ? parseInt(v.textContent, 10) || 0 : 0;
  }

  function handleClick(e) {
    var drawer = getDrawer();
    if (!drawer) return;

    // Close triggers
    if (e.target.closest('.close_cart, .cart-drawer__close')) {
      e.preventDefault();
      closeDrawer();
      return;
    }

    var itemEl = e.target.closest('.cart-item');

    // Quantity minus
    var minusBtn = e.target.closest('[data-cart-minus]');
    if (minusBtn && itemEl) {
      e.preventDefault();
      var qtyDown = currentQty(itemEl) - 1;
      setBusy(itemEl, true);
      changeLine(itemEl.dataset.cartKey, Math.max(0, qtyDown))
        .then(function () { return refresh(); })
        .catch(onError)
        .finally(function () { setBusy(itemEl, false); });
      return;
    }

    // Quantity plus
    var plusBtn = e.target.closest('[data-cart-plus]');
    if (plusBtn && itemEl) {
      e.preventDefault();
      setBusy(itemEl, true);
      changeLine(itemEl.dataset.cartKey, currentQty(itemEl) + 1)
        .then(function () { return refresh(); })
        .catch(onError)
        .finally(function () { setBusy(itemEl, false); });
      return;
    }

    // Remove
    var removeBtn = e.target.closest('[data-cart-remove]');
    if (removeBtn && itemEl) {
      e.preventDefault();
      setBusy(itemEl, true);
      changeLine(itemEl.dataset.cartKey, 0)
        .then(function () { return refresh(); })
        .catch(onError);
      return;
    }

    // Subscribe upgrade
    var subBtn = e.target.closest('[data-cart-subscribe]');
    if (subBtn && itemEl) {
      e.preventDefault();
      var plan = subBtn.dataset.sellingPlan;
      if (!plan) return;
      setBusy(subBtn, true);
      changeLine(itemEl.dataset.cartKey, currentQty(itemEl), { selling_plan: Number(plan) })
        .then(function () { return refresh(); })
        .catch(onError)
        .finally(function () { setBusy(subBtn, false); });
      return;
    }

    // Addon add
    var addBtn = e.target.closest('[data-cart-add]');
    if (addBtn) {
      e.preventDefault();
      var vid = addBtn.dataset.variantId;
      if (!vid) return;
      setBusy(addBtn, true);
      addLine(vid, 1)
        .then(function () { return refresh(); })
        .then(function () { openDrawer(false); })
        .catch(onError)
        .finally(function () { setBusy(addBtn, false); });
      return;
    }
  }

  /* ---- Subscription frequency change ------------------------------------- */
  function handleChange(e) {
    var sel = e.target.closest('[data-plan-select]');
    if (!sel) return;
    var itemEl = sel.closest('.cart-item');
    if (!itemEl) return;
    var plan = sel.value;
    if (!plan) return;
    setBusy(itemEl, true);
    changeLine(itemEl.dataset.cartKey, currentQty(itemEl), { selling_plan: Number(plan) })
      .then(function () { return refresh(); })
      .catch(onError)
      .finally(function () { setBusy(itemEl, false); });
  }

  function onError(err) {
    PubSub.publish(EVENTS.error, err);
    // Re-sync UI with server truth even on error.
    refresh();
  }

  /* ---- Addon carousel: snap-scroll synced with arrows + dots ------------- */
  function initSliders() {
    document.querySelectorAll('[data-addon-slider]').forEach(function (slider) {
      if (slider.__novInit) return;
      slider.__novInit = true;

      var viewport = slider.querySelector('[data-addon-viewport]');
      var track = slider.querySelector('[data-addon-track]');
      var slides = Array.prototype.slice.call(slider.querySelectorAll('[data-addon-slide]'));
      var dots = Array.prototype.slice.call(slider.querySelectorAll('[data-addon-dot]'));
      var prev = slider.querySelector('[data-addon-prev]');
      var next = slider.querySelector('[data-addon-next]');
      if (!track || slides.length === 0) return;

      var index = 0;

      function setActive(i) {
        index = Math.max(0, Math.min(slides.length - 1, i));
        dots.forEach(function (d, di) { d.classList.toggle('is-active', di === index); });
        if (prev) prev.disabled = index === 0;
        if (next) next.disabled = index === slides.length - 1;
      }

      function scrollTo(i) {
        var target = slides[Math.max(0, Math.min(slides.length - 1, i))];
        if (!target) return;
        track.scrollTo({ left: target.offsetLeft - track.offsetLeft, behavior: 'smooth' });
      }

      if (prev) prev.addEventListener('click', function () { scrollTo(index - 1); });
      if (next) next.addEventListener('click', function () { scrollTo(index + 1); });
      dots.forEach(function (dot, di) {
        dot.addEventListener('click', function () { scrollTo(di); });
      });

      // Keep dots/arrows in sync while the user scrolls/snaps.
      if ('IntersectionObserver' in window && viewport) {
        var io = new IntersectionObserver(
          function (entries) {
            entries.forEach(function (entry) {
              if (entry.isIntersecting && entry.intersectionRatio >= 0.6) {
                setActive(slides.indexOf(entry.target));
              }
            });
          },
          { root: viewport, threshold: [0.6] }
        );
        slides.forEach(function (s) { io.observe(s); });
      } else {
        track.addEventListener('scroll', function () {
          var w = track.clientWidth || 1;
          setActive(Math.round(track.scrollLeft / w));
        });
      }

      setActive(0);
    });
  }

  /* ---- Intercept external cart mutations --------------------------------- */
  function onExternalMutation(url) {
    // A cart mutation happened outside the drawer (e.g. product page add).
    refresh().then(function () {
      if (ADD_RE.test(url)) openDrawer(false);
    });
  }

  function bindInterceptors() {
    // jQuery XHR (theme / nuranium.js add-to-cart, qty, remove)
    if (window.jQuery) {
      window.jQuery(document).ajaxComplete(function (_e, xhr, settings) {
        var url = (settings && settings.url) || '';
        if (internalCall === 0 && CART_MUTATION_RE.test(url) && xhr.status >= 200 && xhr.status < 300) {
          onExternalMutation(url);
        }
      });
    }

    // fetch-based add-to-cart (app blocks / custom blocks)
    if (window.fetch && !window.fetch.__novPatched) {
      var nativeFetch = window.fetch;
      var patched = function (input, init) {
        var url = typeof input === 'string' ? input : (input && input.url) || '';
        var promise = nativeFetch.apply(this, arguments);
        if (internalCall === 0 && CART_MUTATION_RE.test(url)) {
          promise.then(function (res) {
            if (res && res.ok) onExternalMutation(url);
          }).catch(function () {});
        }
        return promise;
      };
      patched.__novPatched = true;
      window.fetch = patched;
    }
  }

  /* ---- Open triggers ----------------------------------------------------- */
  function bindOpenTriggers() {
    document.addEventListener('click', function (e) {
      var trigger = e.target.closest('.cart_canvas .header-cart, .cart_canvas .site-header__cart, [data-cart-open]');
      if (trigger) {
        e.preventDefault();
        openDrawer(true);
      }
    });

    // Overlay click + ESC close
    document.addEventListener('click', function (e) {
      if (e.target.classList && e.target.classList.contains('sidebar-overlay')) {
        closeDrawer();
      }
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') closeDrawer();
    });
  }

  /* ---- Init -------------------------------------------------------------- */
  function init() {
    bindOpenTriggers();
    bindInterceptors();
    initSliders();
    localizeShippingBar();
    document.addEventListener('click', handleClick);
    document.addEventListener('change', handleChange);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* ---- Public API -------------------------------------------------------- */
  window.NovCart = {
    open: openDrawer,
    close: closeDrawer,
    refresh: refresh,
    add: addLine,
    change: changeLine,
    subscribe: function (key, plan, qty) {
      return changeLine(key, qty, { selling_plan: Number(plan) });
    },
    on: PubSub.subscribe,
    publish: PubSub.publish,
    events: EVENTS
  };
})();
